import React, { useEffect, useRef, useState } from "react";
import Icon from "../components/AppIcon";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";
import { useAuth } from "../context/AuthContext";
import {
  logoSrc,
  uploadBusinessLogo,
  deleteBusinessLogo,
  updateBusinessSettings,
  changeBusinessPassword,
} from "../api/client";
import { ROOT_DOMAIN } from "../utils/host";
import { sanitizeSubdomainInput, validateSubdomain } from "../utils/subdomain";

// Settings — business branding (logo) + console address (subdomain) + account.
//
// Every surface is tenant-scoped server-side: the organization_id is derived
// from the member's token, never sent by the client. The logo bytes live in
// Postgres; here we upload/replace/remove and the backend returns a fresh,
// cache-busted logoUrl which we patch into the shared auth context so the
// co-branded shell updates immediately.
const MAX_LOGO_BYTES = 1 * 1024 * 1024; // 1 MB — mirrors the backend cap.
const ALLOWED_LOGO_TYPES = ["image/png", "image/jpeg", "image/webp"];

function Card({ children }) {
  return (
    <section className="rounded-xl border border-border bg-card p-6">{children}</section>
  );
}

function CardHead({ icon, title, desc }) {
  return (
    <div className="flex items-start gap-3">
      <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
        <Icon name={icon} size={18} />
      </span>
      <div className="min-w-0">
        <h2 className="text-base font-semibold text-foreground">{title}</h2>
        <p className="mt-0.5 text-xs text-muted-foreground">{desc}</p>
      </div>
    </div>
  );
}

export default function Settings() {
  const { organization, patchOrganization } = useAuth();
  const fileRef = useRef(null);

  const currentLogo = logoSrc(organization?.logoUrl);

  // Logo state
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [logoError, setLogoError] = useState("");
  const [logoSuccess, setLogoSuccess] = useState("");

  // Subdomain state
  const [sub, setSub] = useState(organization?.subdomain || "");
  const [savingSub, setSavingSub] = useState(false);
  const [subError, setSubError] = useState("");
  const [subSuccess, setSubSuccess] = useState("");

  // Keep the local field in sync if the organization loads/refreshes.
  useEffect(() => {
    setSub(organization?.subdomain || "");
  }, [organization?.subdomain]);

  // Company name state
  const [name, setName] = useState(organization?.name || "");
  const [savingName, setSavingName] = useState(false);
  const [nameError, setNameError] = useState("");
  const [nameSuccess, setNameSuccess] = useState("");

  useEffect(() => {
    setName(organization?.name || "");
  }, [organization?.name]);

  const onNameChange = (e) => {
    const { value } = e.target;
    setNameError("");
    setNameSuccess("");
    setName(value);
  };

  const trimmedName = name.trim();
  const nameValid = trimmedName.length >= 2;
  const nameChanged =
    trimmedName !== String(organization?.name || "").trim();

  const onSaveName = async (e) => {
    e.preventDefault();
    setNameError("");
    setNameSuccess("");
    if (!nameValid) {
      setNameError("Please enter at least 2 characters.");
      return;
    }
    setSavingName(true);
    try {
      const res = await updateBusinessSettings({ name: trimmedName });
      patchOrganization({
        name: res.name,
        subdomain: res.subdomain,
        logoUrl: res.logoUrl,
      });
      setNameSuccess("Name updated.");
    } catch (err) {
      setNameError(err?.error || "Could not update the company name.");
    } finally {
      setSavingName(false);
    }
  };

  // Change-password state
  const PW_MIN_LEN = 8;
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [showPw, setShowPw] = useState(false);
  const [savingPw, setSavingPw] = useState(false);
  const [pwError, setPwError] = useState("");
  const [pwSuccess, setPwSuccess] = useState("");

  const onPwChange = (field) => (e) => {
    const { value } = e.target;
    setPwError("");
    setPwSuccess("");
    setPw((p) => ({ ...p, [field]: value }));
  };

  const pwMismatch = Boolean(pw.confirm) && pw.next !== pw.confirm;
  const pwTooShort = Boolean(pw.next) && pw.next.length < PW_MIN_LEN;
  const canSavePw =
    Boolean(pw.current) &&
    pw.next.length >= PW_MIN_LEN &&
    pw.next === pw.confirm &&
    pw.next !== pw.current;

  const onChangePassword = async (e) => {
    e.preventDefault();
    setPwError("");
    setPwSuccess("");
    if (pw.next.length < PW_MIN_LEN) {
      setPwError(`Your new password must be at least ${PW_MIN_LEN} characters.`);
      return;
    }
    if (pw.next !== pw.confirm) {
      setPwError("The new passwords don't match.");
      return;
    }
    if (pw.next === pw.current) {
      setPwError("Your new password must be different from your current one.");
      return;
    }
    setSavingPw(true);
    try {
      await changeBusinessPassword({
        currentPassword: pw.current,
        newPassword: pw.next,
      });
      setPw({ current: "", next: "", confirm: "" });
      setPwSuccess("Password updated.");
    } catch (err) {
      setPwError(err?.error || "Could not change your password.");
    } finally {
      setSavingPw(false);
    }
  };

  const onPickLogo = async (e) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = ""; // allow re-selecting the same file later
    if (!file) return;
    setLogoError("");
    setLogoSuccess("");
    if (!ALLOWED_LOGO_TYPES.includes(file.type)) {
      setLogoError("Please choose a PNG, JPG or WEBP image.");
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      setLogoError("That image is larger than 1 MB. Please choose a smaller file.");
      return;
    }
    setUploading(true);
    try {
      const res = await uploadBusinessLogo(file);
      patchOrganization({ logoUrl: res.logoUrl });
      setLogoSuccess("Logo updated.");
    } catch (err) {
      setLogoError(err?.error || "Could not upload the logo.");
    } finally {
      setUploading(false);
    }
  };

  const onRemoveLogo = async () => {
    setLogoError("");
    setLogoSuccess("");
    setRemoving(true);
    try {
      await deleteBusinessLogo();
      patchOrganization({ logoUrl: "" });
      setLogoSuccess("Logo removed.");
    } catch (err) {
      setLogoError(err?.error || "Could not remove the logo.");
    } finally {
      setRemoving(false);
    }
  };

  const onSubChange = (e) => {
    setSubError("");
    setSubSuccess("");
    setSub(sanitizeSubdomainInput(e.target.value));
  };

  const trimmedSub = sub.trim();
  const subValidationError = validateSubdomain(trimmedSub);
  const subChanged =
    Boolean(trimmedSub) &&
    trimmedSub.toLowerCase() !== String(organization?.subdomain || "").toLowerCase();

  const onSaveSubdomain = async (e) => {
    e.preventDefault();
    setSubError("");
    setSubSuccess("");
    const v = validateSubdomain(trimmedSub);
    if (v) {
      setSubError(v);
      return;
    }
    setSavingSub(true);
    try {
      const res = await updateBusinessSettings({ subdomain: trimmedSub });
      patchOrganization({
        subdomain: res.subdomain,
        name: res.name,
        logoUrl: res.logoUrl,
      });
      setSubSuccess(
        `Saved. Your team now signs in at ${res.subdomain}.${ROOT_DOMAIN}.`
      );
    } catch (err) {
      setSubError(err?.error || "Could not update the console address.");
    } finally {
      setSavingSub(false);
    }
  };

  const busyLogo = uploading || removing;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-foreground">Settings</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Manage how your company appears to your team and your candidates.
        </p>
      </div>

      {/* Company name */}
      <Card>
        <CardHead
          icon="Building2"
          title="Company name"
          desc="How your company is shown across the console, your team's sign-in page, and to your candidates."
        />

        <form onSubmit={onSaveName} className="mt-5 space-y-3">
          <div className="max-w-sm">
            <Input
              label="Name"
              name="companyName"
              value={name}
              onChange={onNameChange}
              error={nameChanged && !nameValid ? "Use at least 2 characters." : ""}
            />
          </div>

          {nameError && (
            <p className="flex items-center gap-1.5 text-sm text-destructive">
              <Icon name="AlertCircle" size={14} /> {nameError}
            </p>
          )}
          {nameSuccess && (
            <p className="flex items-center gap-1.5 text-sm text-success">
              <Icon name="CheckCircle2" size={14} /> {nameSuccess}
            </p>
          )}

          <Button
            type="submit"
            size="sm"
            iconName="Check"
            loading={savingName}
            disabled={savingName || !nameChanged || !nameValid}
          >
            Save name
          </Button>
        </form>
      </Card>

      {/* Branding / logo */}
      <Card>
        <CardHead
          icon="Image"
          title="Logo"
          desc="Shown on your console, your team's sign-in page, and the screen candidates see before and after each interview."
        />

        <div className="mt-5 flex flex-col gap-5 sm:flex-row sm:items-center">
          {/* Preview */}
          <div className="relative flex h-24 w-24 flex-shrink-0 items-center justify-center overflow-hidden rounded-xl border border-border bg-white">
            {currentLogo ? (
              <img
                src={currentLogo}
                alt={`${organization?.name || "Company"} logo`}
                className="h-full w-full object-contain p-2"
              />
            ) : (
              <div className="flex flex-col items-center gap-1 text-muted-foreground">
                <Icon name="ImageOff" size={22} />
                <span className="text-[10px]">No logo</span>
              </div>
            )}
            {busyLogo && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/70">
                <Icon name="Loader2" size={20} className="animate-spin text-primary" />
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="min-w-0 flex-1">
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={onPickLogo}
            />
            <div className="flex flex-wrap items-center gap-2.5">
              <Button
                variant="outline"
                size="sm"
                iconName="Upload"
                onClick={() => fileRef.current?.click()}
                disabled={busyLogo}
                loading={uploading}
              >
                {currentLogo ? "Replace logo" : "Upload logo"}
              </Button>
              {currentLogo && (
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Trash2"
                  onClick={onRemoveLogo}
                  disabled={busyLogo}
                  loading={removing}
                  className="text-muted-foreground hover:text-destructive"
                >
                  Remove
                </Button>
              )}
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              PNG, JPG or WEBP · up to 1 MB · a square image works best.
            </p>
            {logoError && (
              <p className="mt-2 flex items-center gap-1.5 text-sm text-destructive">
                <Icon name="AlertCircle" size={14} /> {logoError}
              </p>
            )}
            {logoSuccess && (
              <p className="mt-2 flex items-center gap-1.5 text-sm text-success">
                <Icon name="CheckCircle2" size={14} /> {logoSuccess}
              </p>
            )}
          </div>
        </div>
      </Card>

      {/* Console address / subdomain */}
      <Card>
        <CardHead
          icon="Globe"
          title="Console address"
          desc="The web address where your team signs in and manages hiring."
        />

        <form onSubmit={onSaveSubdomain} className="mt-5 space-y-3">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
            <div className="sm:max-w-xs sm:flex-1">
              <Input
                label="Subdomain"
                name="subdomain"
                value={sub}
                onChange={onSubChange}
                error={subChanged ? subValidationError : ""}
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
              />
            </div>
            <div className="text-sm text-muted-foreground sm:pt-9">
              <span className="font-medium text-foreground">
                {trimmedSub || "your-company"}.{ROOT_DOMAIN}
              </span>
            </div>
          </div>

          {subError && (
            <p className="flex items-center gap-1.5 text-sm text-destructive">
              <Icon name="AlertCircle" size={14} /> {subError}
            </p>
          )}
          {subSuccess && (
            <p className="flex items-center gap-1.5 text-sm text-success">
              <Icon name="CheckCircle2" size={14} /> {subSuccess}
            </p>
          )}

          <div className="flex items-start gap-2 rounded-lg border border-warning/30 bg-warning/10 px-3 py-2.5 text-xs text-muted-foreground">
            <Icon name="AlertTriangle" size={14} className="mt-0.5 flex-shrink-0 text-warning" />
            <span>
              Changing this updates the sign-in URL for everyone at your company.
              The old address will stop working — share the new one with your team.
            </span>
          </div>

          <Button
            type="submit"
            size="sm"
            iconName="Check"
            loading={savingSub}
            disabled={savingSub || !subChanged || Boolean(subValidationError)}
          >
            Save address
          </Button>
        </form>
      </Card>

      {/* Change password */}
      <Card>
        <CardHead
          icon="Lock"
          title="Change password"
          desc="Update the password you use to sign in to this console."
        />

        <form onSubmit={onChangePassword} className="mt-5 space-y-3">
          <div className="max-w-sm space-y-3">
            <Input
              label="Current password"
              name="currentPassword"
              type={showPw ? "text" : "password"}
              value={pw.current}
              onChange={onPwChange("current")}
              autoComplete="current-password"
            />
            <Input
              label="New password"
              name="newPassword"
              type={showPw ? "text" : "password"}
              value={pw.next}
              onChange={onPwChange("next")}
              error={pwTooShort ? `Use at least ${PW_MIN_LEN} characters.` : ""}
              autoComplete="new-password"
            />
            <Input
              label="Confirm new password"
              name="confirmPassword"
              type={showPw ? "text" : "password"}
              value={pw.confirm}
              onChange={onPwChange("confirm")}
              error={pwMismatch ? "These don't match." : ""}
              autoComplete="new-password"
            />
          </div>

          <button
            type="button"
            onClick={() => setShowPw((v) => !v)}
            className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground"
          >
            <Icon name={showPw ? "EyeOff" : "Eye"} size={14} />
            {showPw ? "Hide passwords" : "Show passwords"}
          </button>

          {pwError && (
            <p className="flex items-center gap-1.5 text-sm text-destructive">
              <Icon name="AlertCircle" size={14} /> {pwError}
            </p>
          )}
          {pwSuccess && (
            <p className="flex items-center gap-1.5 text-sm text-success">
              <Icon name="CheckCircle2" size={14} /> {pwSuccess}
            </p>
          )}

          <Button
            type="submit"
            size="sm"
            iconName="Check"
            loading={savingPw}
            disabled={savingPw || !canSavePw}
          >
            Update password
          </Button>
        </form>
      </Card>
    </div>
  );
}
